const CONFIG_NAME_PATTERN = /^(?:[0-9a-f]{32}_)?aisle-config-(na|eu|fe)-4tier-(?:(\d{4})-)?(.+)$/i;
const MODERN_DUAL_MARKER = /^(.*?)-(?:split)?OVDflexrack-(.*)$/i;
const LEGACY_PARTITIONED_SUFFIX = /^(.*?)-OVDflexrack[^-]*$/i;
const LEGACY_DUAL_MARKER = /^(.*?)-OVD-(.*)$/i;
const STANDARD_OV_SUFFIX = /^(.*?)-splitOVrack$/i;
const LEGACY_OV_SUFFIX = /^(.*?)-OVD$/i;
const UPLOAD_PREFIX = /^[0-9a-f]{32}_/i;
const KNOWN_MODIFIERS = /-(?:6thLightDisabled)$/i;

export const CONFIG_TYPES = {
    BAG: 'bag',
    OV: 'ov',
    FULL_HEIGHT_FLEX: 'full-height-flex',
    PARTITIONED_FLEX: 'partitioned-flex',
    LEGACY_DUAL: 'legacy-dual'
};

export const COLUMN_TYPES = {
    BAG: 'bag',
    OV: 'ov',
    FLEX: 'flex',
    PARTITIONED_FLEX: 'partitioned-flex',
    PILLAR: 'pillar'
};

const stripPathAndExtension = (value) => {
    const fileName = String(value || '').split('/').pop();
    return fileName.replace(UPLOAD_PREFIX, '').replace(/\.json$/i, '');
};

const stripKnownModifiers = (value) => String(value || '').replace(KNOWN_MODIFIERS, '');

/*
 * Tier digits are encoded as <tier><column> pairs, e.g. "3k4k3l4l". Both the canonicaliser and the
 * partitioned-group tokenizer need the same two primitives, so they live here once.
 */

// Reads consecutive <tier><column> pairs starting at startIndex, stopping at the first non-pair.
const readTierColumnPairs = (code, startIndex = 0) => {
    const pairs = [];
    let index = startIndex;

    while (/[1-4]/.test(code[index] || '') && /[a-z]/.test(code[index + 1] || '')) {
        pairs.push({tier: Number(code[index]), column: code[index + 1]});
        index += 2;
    }

    return {pairs, nextIndex: index};
};

// Groups pairs by column, preserving first-appearance order of the columns themselves.
const groupTiersByColumn = (pairs) => {
    const columnOrder = [];
    const tiersByColumn = {};

    pairs.forEach(({tier, column}) => {
        if (!tiersByColumn[column]) {
            tiersByColumn[column] = [];
            columnOrder.push(column);
        }
        tiersByColumn[column].push(tier);
    });

    return {columnOrder, tiersByColumn};
};

export const canonicalizeTierOrder = (code) => String(code || '').replace(/(?:[1-4][a-z])+/g, (group) => {
    const {pairs} = readTierColumnPairs(group);
    const {columnOrder, tiersByColumn} = groupTiersByColumn(pairs);

    return columnOrder.map((column) =>
        [...new Set(tiersByColumn[column])]
            .sort((left, right) => left - right)
            .map((tier) => `${tier}${column}`)
            .join('')
    ).join('');
});

export const parseAisleConfigName = (configName) => {
    const normalizedName = stripPathAndExtension(configName);
    const match = normalizedName.match(CONFIG_NAME_PATTERN);
    if (!match) {
        return null;
    }

    const [, region, year, rawRemainder] = match;
    const remainder = stripKnownModifiers(rawRemainder);
    let aisleCode = remainder;
    let alternateCode = null;
    let configType = CONFIG_TYPES.BAG;
    let namingConvention = 'canonical';

    const modernDual = remainder.match(MODERN_DUAL_MARKER);
    const legacyPartitioned = remainder.match(LEGACY_PARTITIONED_SUFFIX);
    const legacyDual = remainder.match(LEGACY_DUAL_MARKER);
    const standardOv = remainder.match(STANDARD_OV_SUFFIX);
    const legacyOv = remainder.match(LEGACY_OV_SUFFIX);

    if (modernDual) {
        alternateCode = modernDual[1];
        aisleCode = modernDual[2];
        configType = CONFIG_TYPES.FULL_HEIGHT_FLEX;
    } else if (legacyPartitioned) {
        aisleCode = legacyPartitioned[1];
        configType = CONFIG_TYPES.PARTITIONED_FLEX;
        namingConvention = 'legacy';
    } else if (legacyDual) {
        aisleCode = legacyDual[1];
        alternateCode = legacyDual[2];
        configType = CONFIG_TYPES.LEGACY_DUAL;
        namingConvention = 'legacy';
    } else if (standardOv) {
        aisleCode = standardOv[1];
        configType = CONFIG_TYPES.OV;
    } else if (legacyOv) {
        aisleCode = legacyOv[1];
        configType = CONFIG_TYPES.OV;
        namingConvention = 'legacy';
    }

    if (/[1-4][a-z]/.test(aisleCode)) {
        configType = CONFIG_TYPES.PARTITIONED_FLEX;
    } else if (configType === CONFIG_TYPES.BAG && /[A-Z]/.test(aisleCode)) {
        configType = CONFIG_TYPES.OV;
    }

    return {
        name: configName,
        normalizedName,
        region: region.toUpperCase(),
        year: year || null,
        aisleCode,
        canonicalAisleCode: canonicalizeTierOrder(aisleCode),
        alternateCode,
        canonicalAlternateCode: alternateCode ? canonicalizeTierOrder(alternateCode) : null,
        configType,
        namingConvention
    };
};

const tokenizePartitionedGroup = (code, startIndex) => {
    const {pairs, nextIndex: tiersEndIndex} = readTierColumnPairs(code, startIndex);
    const {columnOrder, tiersByColumn} = groupTiersByColumn(pairs);
    let index = tiersEndIndex;

    const footprintCount = Math.ceil(columnOrder.length / 2);
    const ovLabels = [];
    for (let footprint = 0; footprint < footprintCount && /[A-Z]/.test(code[index] || ''); footprint += 1) {
        ovLabels.push(code[index]);
        index += 1;
    }

    const tokens = [];
    for (let footprint = 0; footprint < footprintCount; footprint += 1) {
        const columns = columnOrder.slice(footprint * 2, footprint * 2 + 2);
        tokens.push({
            type: COLUMN_TYPES.PARTITIONED_FLEX,
            columns,
            tiersByColumn,
            ovLabel: ovLabels[footprint] || ''
        });
    }

    return {tokens, nextIndex: index};
};

export const tokenizeAisleCode = (code) => {
    const tokens = [];
    let index = 0;

    while (index < String(code || '').length) {
        const character = code[index];
        if (character === '_') {
            tokens.push({type: COLUMN_TYPES.PILLAR, label: '_'});
            index += 1;
        } else if (/[1-4]/.test(character) && /[a-z]/.test(code[index + 1] || '')) {
            const partitioned = tokenizePartitionedGroup(code, index);
            tokens.push(...partitioned.tokens);
            index = partitioned.nextIndex;
        } else if (/[a-z]/.test(character)) {
            tokens.push({type: COLUMN_TYPES.BAG, label: character});
            index += 1;
        } else if (/[A-Z]/.test(character)) {
            if (/[A-Z]/.test(code[index + 1] || '')) {
                tokens.push({type: COLUMN_TYPES.OV, labels: [character, code[index + 1]], split: true});
                index += 2;
            } else {
                tokens.push({type: COLUMN_TYPES.OV, labels: [character], split: false});
                index += 1;
            }
        } else {
            index += 1;
        }
    }

    return tokens;
};

const getCommonEdges = (bagCode, ovCode) => {
    let prefixLength = 0;
    while (prefixLength < bagCode.length && prefixLength < ovCode.length &&
        bagCode[prefixLength] === ovCode[prefixLength]) {
        prefixLength += 1;
    }

    let suffixLength = 0;
    while (suffixLength < bagCode.length - prefixLength && suffixLength < ovCode.length - prefixLength &&
        bagCode[bagCode.length - 1 - suffixLength] === ovCode[ovCode.length - 1 - suffixLength]) {
        suffixLength += 1;
    }

    return {prefixLength, suffixLength};
};

export const getVisualTokens = (parsedConfig) => {
    if (!parsedConfig) {
        return [];
    }

    if (parsedConfig.configType !== CONFIG_TYPES.FULL_HEIGHT_FLEX || !parsedConfig.alternateCode) {
        return tokenizeAisleCode(parsedConfig.aisleCode);
    }

    const bagCode = parsedConfig.aisleCode;
    const ovCode = parsedConfig.alternateCode;
    const {prefixLength, suffixLength} = getCommonEdges(bagCode, ovCode);
    const suffixStartForBag = bagCode.length - suffixLength;
    const suffixStartForOv = ovCode.length - suffixLength;
    const bagFlex = bagCode.slice(prefixLength, suffixStartForBag).replace(/[^a-z]/g, '');
    const ovFlex = ovCode.slice(prefixLength, suffixStartForOv).replace(/[^A-Z]/g, '');
    const prefix = tokenizeAisleCode(bagCode.slice(0, prefixLength));
    const suffix = tokenizeAisleCode(bagCode.slice(suffixStartForBag));
    const flex = [];

    // One physical full-tier footprint contains two bag columns in bag mode and
    // two vertically stacked OV bins in OV mode.
    for (let index = 0; index < Math.max(bagFlex.length, ovFlex.length); index += 2) {
        flex.push({
            type: COLUMN_TYPES.FLEX,
            bagLabels: bagFlex.slice(index, index + 2).split(''),
            ovLabels: ovFlex.slice(index, index + 2).split('')
        });
    }

    return [...prefix, ...flex, ...suffix];
};

// Column labels are structural tokens shared by the parse and builder paths (they feed the
// structural match signature); the view layer localizes any display wording separately.
export const getVisualColumns = (parsedConfig) => getVisualTokens(parsedConfig).reduce((columns, token) => {
    if (token.type === COLUMN_TYPES.PILLAR) {
        columns.push({type: COLUMN_TYPES.PILLAR, label: 'Pillar'});
    } else if (token.type === COLUMN_TYPES.BAG) {
        columns.push({type: COLUMN_TYPES.BAG, label: token.label, bagTiers: [1, 2, 3, 4]});
    } else if (token.type === COLUMN_TYPES.OV) {
        // One token = one physical column position: a split pair (top, bottom) or a full bin.
        columns.push({
            type: COLUMN_TYPES.OV,
            label: token.labels.join(' / '),
            ovLabels: [...token.labels],
            split: token.labels.length === 2,
            bagTiers: []
        });
    } else if (token.type === COLUMN_TYPES.FLEX) {
        const bagLabels = token.bagLabels || [token.bagLabel].filter(Boolean);
        const ovLabels = token.ovLabels || [token.ovLabel].filter(Boolean);
        columns.push({
            type: COLUMN_TYPES.FLEX,
            label: bagLabels.join(' + ') || '?',
            bagLabels,
            ovLabels,
            bagTiers: [1, 2, 3, 4]
        });
    } else if (token.type === COLUMN_TYPES.PARTITIONED_FLEX) {
        token.columns.forEach((column) => columns.push({
            type: COLUMN_TYPES.PARTITIONED_FLEX,
            label: token.ovLabel ? `${column} / ${token.ovLabel}` : column,
            bagTiers: [...new Set(token.tiersByColumn[column] || [])].sort((left, right) => left - right)
        }));
    }
    return columns;
}, []);

export const filterAisleConfigs = (configNames, {query = '', region = 'ALL', year = 'ALL'} = {}) => {
    const normalizedQuery = query.trim().toLowerCase();
    return (configNames || [])
        .map((name) => ({name, parsed: parseAisleConfigName(name)}))
        .filter(({name, parsed}) => {
            if (!parsed) {
                return false;
            }
            const queryMatches = !normalizedQuery || [
                name,
                parsed.aisleCode,
                parsed.alternateCode || '',
                parsed.canonicalAisleCode,
                parsed.canonicalAlternateCode || ''
            ].some((value) => String(value).toLowerCase().includes(normalizedQuery));
            return queryMatches && (region === 'ALL' || parsed.region === region) &&
                (year === 'ALL' || parsed.year === year);
        })
        .sort((left, right) => String(left.name).localeCompare(String(right.name)))
        .map(({name}) => name);
};

export const ALLOWED_COLUMN_LABELS = [
    'a', 'b', 'c', 'd', 'e', 'g', 'h', 'j', 'k', 'l', 'm', 'n',
    'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'
];

export const BUILDER_COLUMN_MODES = {
    ABSENT: 'absent',
    BAG: COLUMN_TYPES.BAG,
    OV: COLUMN_TYPES.OV,
    FLEX: COLUMN_TYPES.FLEX
};

export const createInitialColumnModes = () => ALLOWED_COLUMN_LABELS.reduce((modes, label) => ({
    ...modes,
    [label]: BUILDER_COLUMN_MODES.ABSENT
}), {});

export const cycleColumnMode = (mode) => ({
    [BUILDER_COLUMN_MODES.ABSENT]: BUILDER_COLUMN_MODES.BAG,
    [BUILDER_COLUMN_MODES.BAG]: BUILDER_COLUMN_MODES.OV,
    [BUILDER_COLUMN_MODES.OV]: BUILDER_COLUMN_MODES.FLEX,
    [BUILDER_COLUMN_MODES.FLEX]: BUILDER_COLUMN_MODES.ABSENT
}[mode] || BUILDER_COLUMN_MODES.BAG);

export const DEFAULT_FLEX_OV_LETTERS = ['T', 'U', 'P', 'R', 'W', 'X', 'Y', 'Z', 'S', 'V', 'Q', 'N'];

export const getSelectedFlexLabels = (columnModes) => ALLOWED_COLUMN_LABELS.filter((label) =>
    columnModes[label] === BUILDER_COLUMN_MODES.FLEX
);

// Generator ground truth: a full-tier flex zone is a chain of footprints, each made of
// exactly TWO bag columns that convert together into TWO stacked OV bins (top, bottom).
// The builder supports full-height flex only; half-height (partitioned) configs remain
// parse/preview-only for existing production filenames.
export const chunkFlexFootprints = (flexLabels) => {
    const footprints = [];
    for (let index = 0; index < (flexLabels || []).length; index += 2) {
        footprints.push(flexLabels.slice(index, index + 2));
    }
    return footprints;
};

export const resolveFlexOvPairs = (footprintCount, overridePairs = [], usedLetters = []) => {
    const taken = new Set(usedLetters.map((letter) => String(letter || '').toUpperCase()));
    overridePairs.forEach((pair) => (pair || []).forEach((letter) =>
        letter && taken.add(String(letter).toUpperCase())));
    const available = DEFAULT_FLEX_OV_LETTERS.filter((letter) => !taken.has(letter));
    const pairs = [];
    for (let index = 0; index < footprintCount; index += 1) {
        const override = overridePairs[index];
        if (override && override[0] && override[1]) {
            pairs.push([String(override[0]).toUpperCase(), String(override[1]).toUpperCase()]);
        } else {
            pairs.push([available.shift() || '?', available.shift() || '?']);
        }
    }
    return pairs;
};

const getModeCharacter = (label, mode) => {
    if (mode === BUILDER_COLUMN_MODES.BAG) {
        return label;
    }
    if (mode === BUILDER_COLUMN_MODES.OV) {
        return label.toUpperCase();
    }
    return '';
};

const appendPillarTo = (code, label, pillars) => pillars.includes(label) && code ? `${code}_` : code;

// Validation errors are machine-readable {code, params} descriptors: the model stays free
// of user-facing prose so the UI can localize every message (react-intl) from the code.
export const BUILDER_VALIDATION_CODES = {
    FLEX_PAIRS_REQUIRED: 'FLEX_PAIRS_REQUIRED',
    FLEX_NOT_ADJACENT: 'FLEX_NOT_ADJACENT',
    PILLAR_INSIDE_FLEX_ZONE: 'PILLAR_INSIDE_FLEX_ZONE',
    FLEX_OV_LABELS_NOT_UNIQUE: 'FLEX_OV_LABELS_NOT_UNIQUE',
    FLEX_OV_LABEL_REUSED: 'FLEX_OV_LABEL_REUSED',
    NO_COLUMNS_SELECTED: 'NO_COLUMNS_SELECTED'
};

const validateFlexSelection = ({flexLabels, ovPairs, columnModes, pillars}) => {
    if (flexLabels.length === 0) {
        return [];
    }

    const errors = [];
    if (flexLabels.length % 2 !== 0) {
        errors.push({code: BUILDER_VALIDATION_CODES.FLEX_PAIRS_REQUIRED, params: {count: flexLabels.length}});
    }

    const activeLabels = ALLOWED_COLUMN_LABELS.filter((label) =>
        columnModes[label] !== BUILDER_COLUMN_MODES.ABSENT);
    const zoneStart = activeLabels.indexOf(flexLabels[0]);
    if (!flexLabels.every((label, index) => activeLabels.indexOf(label) === zoneStart + index)) {
        errors.push({code: BUILDER_VALIDATION_CODES.FLEX_NOT_ADJACENT, params: {columns: flexLabels.join(', ')}});
    }
    const pillarsInsideZone = flexLabels.slice(0, -1).filter((label) => pillars.includes(label));
    if (pillarsInsideZone.length > 0) {
        errors.push({
            code: BUILDER_VALIDATION_CODES.PILLAR_INSIDE_FLEX_ZONE,
            params: {columns: pillarsInsideZone.join(', ')}
        });
    }

    const flatLetters = ovPairs.reduce((letters, pair) => [...letters, ...pair], []);
    if (ovPairs.some((pair) => pair[0] === pair[1]) ||
        new Set(flatLetters).size !== flatLetters.length) {
        errors.push({code: BUILDER_VALIDATION_CODES.FLEX_OV_LABELS_NOT_UNIQUE, params: {}});
    }
    const reusedLetters = flatLetters.filter((letter) => {
        const gridMode = columnModes[String(letter).toLowerCase()];
        return gridMode === BUILDER_COLUMN_MODES.BAG || gridMode === BUILDER_COLUMN_MODES.OV;
    });
    if (reusedLetters.length > 0) {
        errors.push({
            code: BUILDER_VALIDATION_CODES.FLEX_OV_LABEL_REUSED,
            params: {letters: [...new Set(reusedLetters)].join(', ')}
        });
    }
    return errors;
};

const buildRackCodes = ({columnModes, pillars, flexLabels, ovPairs}) => {
    const footprints = chunkFlexFootprints(flexLabels);
    const lastFlexLabel = flexLabels[flexLabels.length - 1];
    let bagCode = '';
    let ovCode = '';
    let zoneEmitted = false;

    ALLOWED_COLUMN_LABELS.forEach((label) => {
        const mode = columnModes[label];
        if (mode === BUILDER_COLUMN_MODES.ABSENT) {
            return;
        }
        if (mode === BUILDER_COLUMN_MODES.FLEX) {
            if (!zoneEmitted) {
                bagCode += flexLabels.join('');
                ovCode += footprints.map((footprint, index) => (ovPairs[index] || []).join('')).join('');
                zoneEmitted = true;
            }
            if (label === lastFlexLabel) {
                bagCode = appendPillarTo(bagCode, label, pillars);
                ovCode = appendPillarTo(ovCode, label, pillars);
            }
            return;
        }
        const character = getModeCharacter(label, mode);
        bagCode = appendPillarTo(`${bagCode}${character}`, label, pillars);
        ovCode = appendPillarTo(`${ovCode}${character}`, label, pillars);
    });

    if (flexLabels.length === 0) {
        return {aisleCode: bagCode, bagCode: null, ovCode: null};
    }
    return {aisleCode: bagCode, bagCode, ovCode};
};

const buildBuilderVisualColumns = ({columnModes, pillars, flexLabels, ovPairs}) => {
    const footprints = chunkFlexFootprints(flexLabels);
    const lastFlexLabel = flexLabels[flexLabels.length - 1];
    const columns = [];
    let zoneEmitted = false;
    let ovRun = [];

    // Consecutive OV letters pair into split-OV columns (first letter = top bin, second =
    // bottom bin); a trailing unpaired letter is one full-height OV bin. Pillars, bag
    // columns, and flex zones break the pairing run, matching the generator tokenizer.
    const flushOvRun = () => {
        for (let index = 0; index < ovRun.length; index += 2) {
            const binLabels = ovRun.slice(index, index + 2);
            columns.push({
                type: COLUMN_TYPES.OV,
                label: binLabels.join(' / '),
                ovLabels: binLabels,
                split: binLabels.length === 2,
                bagTiers: []
            });
        }
        ovRun = [];
    };
    const pushPillar = (label) => {
        if (pillars.includes(label)) {
            columns.push({type: COLUMN_TYPES.PILLAR, label: 'Pillar'});
        }
    };

    ALLOWED_COLUMN_LABELS.forEach((label) => {
        const mode = columnModes[label];
        if (mode === BUILDER_COLUMN_MODES.ABSENT) {
            return;
        }
        if (mode === BUILDER_COLUMN_MODES.OV) {
            ovRun.push(label.toUpperCase());
            if (pillars.includes(label)) {
                flushOvRun();
                pushPillar(label);
            }
            return;
        }
        flushOvRun();
        if (mode === BUILDER_COLUMN_MODES.FLEX) {
            if (!zoneEmitted) {
                footprints.forEach((footprint, index) => {
                    columns.push({
                        type: COLUMN_TYPES.FLEX,
                        label: footprint.join(' + '),
                        bagLabels: footprint,
                        ovLabels: ovPairs[index] || ['?', '?'],
                        bagTiers: [1, 2, 3, 4]
                    });
                });
                zoneEmitted = true;
            }
            if (label === lastFlexLabel) {
                pushPillar(label);
            }
            return;
        }
        columns.push({type: COLUMN_TYPES.BAG, label, bagTiers: [1, 2, 3, 4]});
        pushPillar(label);
    });
    flushOvRun();
    return columns;
};

export const buildRackDescription = ({
    region,
    columnModes,
    pillars = [],
    flexOvLabels = []
}) => {
    const flexLabels = getSelectedFlexLabels(columnModes);
    const footprintCount = Math.ceil(flexLabels.length / 2);
    const gridOvLetters = ALLOWED_COLUMN_LABELS
        .filter((label) => columnModes[label] === BUILDER_COLUMN_MODES.OV)
        .map((label) => label.toUpperCase());
    const ovPairs = flexLabels.length === 0 ? [] :
        resolveFlexOvPairs(footprintCount, flexOvLabels, gridOvLetters);
    const errors = validateFlexSelection({flexLabels, ovPairs, columnModes, pillars});
    const codes = buildRackCodes({columnModes, pillars, flexLabels, ovPairs});

    const normalizedRegion = String(region || '').toUpperCase();
    // The generator stamps the current year into new filenames; matching ignores years.
    const baseName = `aisle-config-${normalizedRegion.toLowerCase()}-4tier-${new Date().getFullYear()}`;
    let candidateName;
    if (flexLabels.length > 0) {
        const marker = normalizedRegion === 'EU' ? 'splitOVDflexrack' : 'SplitOVDflexrack';
        candidateName = `${baseName}-${codes.ovCode}-${marker}-${codes.bagCode}`;
    } else if (/[A-Z]/.test(codes.aisleCode)) {
        candidateName = `${baseName}-${codes.aisleCode}-splitOVrack`;
    } else {
        candidateName = `${baseName}-${codes.aisleCode}`;
    }

    const columns = buildBuilderVisualColumns({columnModes, pillars, flexLabels, ovPairs});
    if (columns.length === 0) {
        errors.push({code: BUILDER_VALIDATION_CODES.NO_COLUMNS_SELECTED, params: {}});
    }

    return {
        region: normalizedRegion,
        aisleCode: codes.aisleCode,
        bagCode: codes.bagCode,
        ovCode: codes.ovCode,
        candidateName,
        columns,
        flexLabels,
        footprints: chunkFlexFootprints(flexLabels).map((bagLabels, index) => ({
            bagLabels,
            ovLabels: ovPairs[index] || []
        })),
        errors
    };
};

export const getVisualStructureSignature = (columns) => JSON.stringify((columns || []).map((column) => ({
    type: column.type,
    label: column.label,
    bagLabels: [...(column.bagLabels || [])],
    ovLabels: [...(column.ovLabels || [])],
    bagTiers: [...(column.bagTiers || [])].sort((left, right) => left - right)
})));

// Year-agnostic on purpose: the same physical rack keeps matching a config generated in any
// prior year, and legacy names without a year segment are equally valid matches.
export const findConfigsByRackDescription = (configNames, rackDescription) => {
    if (!rackDescription || rackDescription.errors.length > 0) {
        return [];
    }
    const requestedSignature = getVisualStructureSignature(rackDescription.columns);
    return (configNames || []).filter((name) => {
        const parsed = parseAisleConfigName(name);
        return parsed && parsed.region === rackDescription.region &&
            getVisualStructureSignature(getVisualColumns(parsed)) === requestedSignature;
    }).sort((left, right) => String(left).localeCompare(String(right)));
};



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/aisleconfig/AisleConfigModel.js